
import { LocaleLink } from "@/components/site/LocaleLink";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, RevealItem } from "@/components/site/Reveal";

const PLANS = [
  {
    name: "Start",
    audience: "Media monitoring for one account in Bulgaria across the channels that matter.",
    features: [
      "One account monitored in Bulgaria",
      "Coverage of print, online, radio, TV and selected social/media channels",
      "Access to Perceptica's Media Listening Platform (MLP) with configurable dashboards, analytics views and exports",
      "Up to 3 months of historical data access",
      "Automated newsletters, alerts and account-managed support",
    ],
    plan: "start",
  },
  {
    name: "Advanced",
    audience: "Advanced media monitoring for teams running multiple brands or campaigns in Bulgaria.",
    features: [
      "Up to three accounts monitored in Bulgaria",
      "Coverage of print, online, broadcast and selected social/media channels",
      "Enhanced reporting in MLP: dashboards, exports, analytics insights and alerts",
      "Automated and curated newsletter delivery",
      "Up to 6 months of historical data access and enrichment for deeper campaign tracking",
    ],
    plan: "advanced",
    featured: true,
  },
  {
    name: "Enterprise",
    audience: "Enterprise-grade media intelligence across Bulgaria and the wider South-Eastern Europe region.",
    features: [
      "Up to 5 accounts across Bulgaria and SEE",
      "Coverage of print, online, broadcast and selected social/media channels",
      "Advanced analytics: configurable dashboards, exports, alerts, automated and custom newsletters, enrichment tools",
      "Cross-market media insights and multi-country monitoring",
      "Extended historical data access for regional analysis and strategic reporting",
    ],
    plan: "enterprise",
  },
];

export function PlansTeaser() {
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground py-24 lg:py-32">
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -right-32 h-[460px] w-[460px] rounded-full opacity-30 blur-[120px]"
        style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
      />
      <div className="noise-overlay" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal className="mb-12 max-w-3xl">
          <h2 className="font-display text-[clamp(1.75rem,2.6vw,2.25rem)] text-white">
            A plan shaped to your team's stage
          </h2>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-white/70">
            Every plan includes onboarding, EU data residency and a dedicated support team. Pricing is
            tailored to the brands and markets you need to cover.
          </p>
        </Reveal>

        <Reveal stagger className="grid gap-6 lg:grid-cols-3">
          {PLANS.map((p) => (
            <RevealItem key={p.name} className="h-full">
              <article
                className={
                  "gradient-border relative flex h-full flex-col rounded-2xl bg-white/[0.03] p-8 backdrop-blur-sm" +
                  (p.featured ? " lg:-translate-y-3 lg:shadow-[var(--shadow-glow)]" : "")
                }
              >
                {p.featured && (
                  <span className="absolute -top-3 right-6 inline-flex items-center rounded-full bg-gradient-brand px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white shadow-pill">
                    Most popular
                  </span>
                )}
                <h3 className="font-display text-[1.5rem] tracking-[-0.02em] text-white">{p.name}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-white/65">{p.audience}</p>

                <ul className="mt-6 flex-1 space-y-3 text-[13.5px] text-white/85">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check className="mt-0.5 h-4 w-4 flex-none text-accent" aria-hidden />
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="mt-8">
                  <Button asChild variant={p.featured ? "default" : "outlineLight"} className="w-full">
                    <LocaleLink to="/request-a-demo" search={{ plan: p.plan }}>
                      Contact for pricing
                    </LocaleLink>
                  </Button>
                </div>
              </article>
            </RevealItem>
          ))}
        </Reveal>

        <p className="mt-10 text-center text-[12.5px] text-white/60">
          All plans include onboarding, EU data residency, and dedicated support.
        </p>
      </div>
    </section>
  );
}

/**
 * Layout-matched fallback for <PlansTeaser />.
 * Mirrors section padding, container width, header block and 3-card grid,
 * including the featured card's lg:-translate-y-3 offset, so swap-in is CLS-free.
 */
export function PlansTeaserSkeleton() {
  return (
    <section aria-hidden className="relative overflow-hidden bg-primary text-primary-foreground py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-12 max-w-3xl space-y-4">
          <div className="h-8 w-2/3 animate-pulse rounded-md bg-white/5 lg:h-10" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-white/5" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {[false, true, false].map((featured, i) => (
            <div
              key={i}
              className={
                "flex h-[480px] flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-8" +
                (featured ? " lg:-translate-y-3 lg:shadow-[var(--shadow-glow)]" : "")
              }
            >
              <div className="h-7 w-24 animate-pulse rounded bg-white/10" />
              <div className="mt-2 space-y-2">
                <div className="h-3 w-full animate-pulse rounded bg-white/5" />
                <div className="h-3 w-5/6 animate-pulse rounded bg-white/5" />
              </div>
              <ul className="mt-6 flex-1 space-y-3">
                {Array.from({ length: 5 }).map((__, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <div className="mt-0.5 h-4 w-4 flex-none animate-pulse rounded bg-white/10" />
                    <div className="h-3 flex-1 animate-pulse rounded bg-white/5" />
                  </li>
                ))}
              </ul>
              <div className="mt-8 h-10 w-full animate-pulse rounded-md bg-white/10" />
            </div>
          ))}
        </div>
        <div className="mx-auto mt-10 h-3 w-1/2 animate-pulse rounded bg-white/5" />
      </div>
    </section>
  );
}
